// ACADEMY landing — vzdělávací sekce s kategoriemi, ne jen výpis blogu.
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { GraduationCap } from "@phosphor-icons/react";
import AppShell from "../components/AppShell";
import { useLang } from "../contexts/LanguageContext";
import { blogPublicList } from "../lib/blog-api";

const ACADEMY_I18N = {
    cs: {
        h1a: "Nauč se přemýšlet jako", h1b: "analytik, scout a sportovní ředitel",
        desc: "Články, návody, scoutingové reporty, datová metodika a týdenní FPL obsah. Vše propojené s nástroji a profily hráčů.",
        serial: "Seriál", clubOpps: "Club Opportunities — FPL příležitosti klub po klubu", open: "Otevřít →",
        scouting: "Scouting", focus: "Fokus seznamy — kurátorované transfer targets podle dat",
        loading: "Načítám…", empty: "V této kategorii zatím nic není — brzy přibude.",
    },
    en: {
        h1a: "Learn to think like an", h1b: "analyst, scout and sporting director",
        desc: "Articles, guides, scouting reports, data methodology and weekly FPL content. All connected to our tools and player profiles.",
        serial: "Series", clubOpps: "Club Opportunities — FPL value club by club", open: "Open →",
        scouting: "Scouting", focus: "Focus lists — data-driven curated transfer targets",
        loading: "Loading…", empty: "Nothing in this category yet — coming soon.",
    },
};

export const ACADEMY_CATEGORIES = [
    { key: "latest", label: "Latest", desc: "Nejnovější obsah napříč kategoriemi" },
    { key: "strategy", label: "Strategy", desc: "Strategie FPL manažera" },
    { key: "players", label: "Players", desc: "Scouting reporty, wonderkids a hráčské analýzy" },
    { key: "data_lab", label: "Data Lab", desc: "Metriky, FDR, timing a metodika nástrojů" },
    { key: "weekly", label: "Weekly", desc: "Pravidelný obsah před deadlinem" },
    { key: "guides", label: "Guides", desc: "Návody k používání Fantasy Academy" },
];

export default function Academy() {
    const [posts, setPosts] = useState(null);
    const [cat, setCat] = useState("latest");
    const { lang } = useLang();
    const T = ACADEMY_I18N[lang] || ACADEMY_I18N.cs;

    useEffect(() => {
        document.title = "Academy — nauč se přemýšlet jako analytik | Fantasy Academy";
        blogPublicList().then((d) => setPosts(d.posts)).catch(() => setPosts([]));
    }, []);

    const filtered = useMemo(() => {
        if (!posts) return null;
        if (cat === "latest") return posts;
        return posts.filter((p) => (p.category || "players") === cat);
    }, [posts, cat]);

    const activeCat = ACADEMY_CATEGORIES.find((c) => c.key === cat);

    return (
        <AppShell>
            <div className="max-w-5xl mx-auto space-y-8" data-testid="academy-page">
                <header className="border border-fa-cream/20 bg-gradient-to-br from-fa-cream/5 via-transparent to-transparent p-6 md:p-8">
                    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-fa-cream flex items-center gap-2">
                        <GraduationCap size={12} weight="fill" /> Academy
                    </div>
                    <h1 className="font-display font-black uppercase tracking-tight text-3xl md:text-4xl mt-2">
                        {T.h1a} <span className="text-fa-cream">{T.h1b}</span>
                    </h1>
                    <p className="font-mono text-[11px] text-white/60 mt-3 max-w-2xl leading-relaxed">{T.desc}</p>
                </header>

                <Link to="/academy/serie/club-opportunities" data-testid="academy-club-opps-banner"
                      className="flex items-center gap-3 border border-fa-mint/40 bg-fa-mint/5 px-4 py-3 hover:bg-fa-mint/15 transition-colors">
                    <span className="font-mono text-[9px] uppercase tracking-widest px-2 py-0.5 bg-fa-mint text-black font-bold">{T.serial}</span>
                    <span className="font-display font-black uppercase text-sm">{T.clubOpps}</span>
                    <span className="font-mono text-[10px] text-fa-mint ml-auto">{T.open}</span>
                </Link>

                <Link to="/fokus" data-testid="academy-focus-lists-banner"
                      className="flex items-center gap-3 border border-fa-yellow/40 bg-fa-yellow/5 px-4 py-3 hover:bg-fa-yellow/15 transition-colors">
                    <span className="font-mono text-[9px] uppercase tracking-widest px-2 py-0.5 bg-fa-yellow text-black font-bold">{T.scouting}</span>
                    <span className="font-display font-black uppercase text-sm">{T.focus}</span>
                    <span className="font-mono text-[10px] text-fa-yellow ml-auto">{T.open}</span>
                </Link>

                <div className="flex flex-wrap gap-1.5" data-testid="academy-categories">
                    {ACADEMY_CATEGORIES.map((c) => (
                        <button key={c.key} type="button" onClick={() => setCat(c.key)}
                                data-testid={`academy-cat-${c.key}`}
                                className={`font-mono text-[10px] uppercase tracking-widest px-3 py-2 border transition-colors ${cat === c.key ? "bg-fa-cream text-black border-fa-cream font-bold" : "border-white/15 text-white/60 hover:text-white"}`}>
                            {c.label}
                        </button>
                    ))}
                </div>
                {activeCat && <p className="font-mono text-[10px] text-white/40 -mt-4">{activeCat.desc}</p>}

                {filtered === null && <p className="font-mono text-[11px] text-white/40">{T.loading}</p>}
                {filtered?.length === 0 && (
                    <p className="font-mono text-[11px] text-white/40 py-6" data-testid="academy-empty">{T.empty}</p>
                )}
                <div className="space-y-3" data-testid="academy-posts">
                    {(filtered || []).map((p) => (
                        <Link key={p.id} to={`/academy/${p.slug}`} data-testid={`academy-card-${p.slug}`}
                              className="flex gap-4 border border-white/10 bg-[#0d0d0d] p-3 hover:border-fa-cream/50 transition-colors">
                            <img src={`${process.env.REACT_APP_BACKEND_URL}/api/blog/${p.hero_status === "ready" ? "hero" : "og"}/${p.slug}.png`} alt={p.title}
                                 className="hidden sm:block w-44 h-[92px] object-cover border border-white/10 shrink-0" loading="lazy" />
                            <div className="min-w-0">
                                <div className="flex items-center gap-2 font-mono text-[9px] uppercase tracking-widest text-white/40 flex-wrap">
                                    <span className="text-fa-cream">{ACADEMY_CATEGORIES.find((c) => c.key === (p.category || "players"))?.label}</span>
                                    <span>{p.published_at ? new Date(p.published_at).toLocaleDateString("cs") : ""}</span>
                                    {p.player_name && <span className="text-fa-mint">{p.player_name}</span>}
                                    {(p.tags || []).slice(0, 3).map((t) => <span key={t} className="border border-white/15 px-1.5 py-0.5">{t}</span>)}
                                </div>
                                <h2 className="font-display font-black text-xl mt-1">{p.title}</h2>
                                <p className="text-white/60 text-sm mt-1">{p.perex}</p>
                            </div>
                        </Link>
                    ))}
                </div>
            </div>
        </AppShell>
    );
}
